import { FC, useEffect, useMemo, useState } from 'react';
import { Button, ComboBox, InlineNotification, TextInput } from '@carbon/react';
import { Add, TrashCan } from '@carbon/react/icons';
import { useAtom, useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';
import type {
  GeologicIntervalValidationError,
  GeologicIntervalValidationWarning,
  OperatorSearchResult,
} from 'types/api-responses';
import { useOperatorsSearchAtom, validateGeologicIntervalAtom } from './atoms';
import type { GeologicIntervalRow, PendingAddOnc } from './types';
import {
  buildDuplicateWarnText,
  buildDuplicateWarningSubtitle,
  buildValidationErrorSubtitle,
} from './utils';
import styles from './geologic-interval-drawer.module.scss';

const DUPLICATE_WARNING_CODE = 'duplicate_target_zone_identifier_in_basin';

const ALL_OPERATORS_LABEL = 'All operators';
const ALL_OPERATORS_ITEM: OperatorSearchResult = {
  name: ALL_OPERATORS_LABEL,
  entityId: null,
};

type SerializedOperator = { name: string | null; allOperators: boolean };

// "All operators" sentinel → `{ name: null, allOperators: true }`.
function serializeOperator(item: OperatorSearchResult): SerializedOperator {
  if (item.name === ALL_OPERATORS_LABEL) {
    return { name: null, allOperators: true };
  }
  return { name: item.name, allOperators: false };
}

export interface OperatorNamingConventionsSectionProps {
  target: GeologicIntervalRow;
  // Staged state lives in the parent so the Edit drawer's Save can fold
  // adds + deletes into the same PATCH.
  pendingDeleteIds: number[];
  onTogglePendingDelete: (id: number) => void;
  pendingAddOncs: PendingAddOnc[];
  onAddPending: (
    operator: SerializedOperator,
    targetZoneIdentifier: string
  ) => void;
  onRemovePending: (tempId: string) => void;
}

// Presentational. Parent owns staged adds + deletes; this component just
// renders + emits intents.
export const OperatorNamingConventionsSection: FC<
  OperatorNamingConventionsSectionProps
> = ({
  target,
  pendingDeleteIds,
  onTogglePendingDelete,
  pendingAddOncs,
  onAddPending,
  onRemovePending,
}) => {
  const [isAddOpen, setIsAddOpen] = useState(false);

  const visibleOncs = target.operatorNamingConventions.filter(
    (o) => !pendingDeleteIds.includes(o.id)
  );

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Operator Naming Convention</h3>
      <p className={styles.sectionHelp}>
        Map an operator&rsquo;s Target Zone Identifier to this geologic
        interval. This is used in Target Zone identification.
      </p>

      <ul className={styles.oncList}>
        {visibleOncs.map((onc) => (
          <OncCard
            key={`existing-${onc.id}`}
            operatorLabel={
              onc.operator.allOperators ? 'All Operators' : onc.operator.name
            }
            targetZoneIdentifier={onc.targetZoneIdentifier}
            trashLabel="Delete naming convention"
            onTrash={() => onTogglePendingDelete(onc.id)}
          />
        ))}
        {pendingAddOncs.map((p) => (
          <OncCard
            key={`pending-${p.tempId}`}
            operatorLabel={
              p.operator.allOperators ? 'All Operators' : p.operator.name
            }
            targetZoneIdentifier={p.targetZoneIdentifier}
            trashLabel="Discard naming convention"
            onTrash={() => onRemovePending(p.tempId)}
          />
        ))}
      </ul>

      {isAddOpen ? (
        <AddOncForm
          target={target}
          onCancel={() => setIsAddOpen(false)}
          onAdd={(operator, targetZoneIdentifier) => {
            onAddPending(operator, targetZoneIdentifier);
            setIsAddOpen(false);
          }}
        />
      ) : (
        <Button
          kind="tertiary"
          size="sm"
          renderIcon={Add}
          onClick={() => setIsAddOpen(true)}
          className={styles.oncAddButton}
        >
          Add Operator Naming Convention
        </Button>
      )}
    </section>
  );
};

interface OncCardProps {
  operatorLabel: string | null;
  targetZoneIdentifier: string;
  trashLabel: string;
  onTrash: () => void;
}

const OncCard: FC<OncCardProps> = ({
  operatorLabel,
  targetZoneIdentifier,
  trashLabel,
  onTrash,
}) => (
  <li className={styles.oncCard}>
    <div className={styles.oncCardBody}>
      <span className={styles.oncCardLine}>
        <span className={styles.oncCardLabel}>Operator: </span>
        {operatorLabel}
      </span>
      <span className={styles.oncCardLine}>
        <span className={styles.oncCardLabel}>Target Zone Identifier: </span>
        {targetZoneIdentifier}
      </span>
    </div>
    <Button
      hasIconOnly
      size="sm"
      kind="ghost"
      iconDescription={trashLabel}
      tooltipAlignment="end"
      tooltipPosition="left"
      renderIcon={(props) => <TrashCan size={16} {...props} />}
      onClick={onTrash}
    />
  </li>
);

interface AddOncFormProps {
  target: GeologicIntervalRow;
  onCancel: () => void;
  onAdd: (operator: SerializedOperator, targetZoneIdentifier: string) => void;
}

// Inline add-form panel. Owns the per-mount operator-search atom and the
// debounced validation probe. Warnings are non-blocking; only errors disable Add.
const AddOncForm: FC<AddOncFormProps> = ({ target, onCancel, onAdd }) => {
  const operatorsSearch = useOperatorsSearchAtom();
  const [operatorsState, setOperatorsPayload] = useAtom(
    operatorsSearch.searchAtom
  );
  const resetOperatorsSearch = useSetAtom(operatorsSearch.resetSearchDataAtom);

  const [selectedOperator, setSelectedOperator] =
    useState<OperatorSearchResult | null>(null);
  const [targetZoneIdentifier, setTargetZoneIdentifier] = useState('');
  const [operatorInputEmpty, setOperatorInputEmpty] = useState(true);
  // Snapshot of the empty-text response so a cleared input restores the
  // original list without a fresh fetch.
  const [initialOperators, setInitialOperators] = useState<
    OperatorSearchResult[] | null
  >(null);

  // Prime the list with an empty-text query so the combo is browsable
  // before the user types anything.
  useEffect(() => {
    setOperatorsPayload((current) => ({
      ...(current ?? {}),
      text: '',
      pageSize: 20,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const results = operatorsState?.data?.results;
    if (initialOperators === null && Array.isArray(results)) {
      setInitialOperators(results);
    }
  }, [operatorsState?.data?.results, initialOperators]);

  const [validateState, validateInterval] = useAtom(
    validateGeologicIntervalAtom
  );
  const [duplicateWarning, setDuplicateWarning] =
    useState<GeologicIntervalValidationWarning | null>(null);
  // Surfaced inline so the user sees a `taken` conflict before clicking Add
  // rather than only after the mutation 422s.
  const [validationError, setValidationError] =
    useState<GeologicIntervalValidationError | null>(null);

  const debouncedSearchOperators = useMemo(
    () =>
      debounce((text: string) => {
        setOperatorsPayload((current) => ({
          ...(current ?? {}),
          text,
          pageSize: 20,
        }));
      }, 400),
    [setOperatorsPayload]
  );

  useEffect(() => {
    return () => {
      debouncedSearchOperators.cancel();
    };
  }, [debouncedSearchOperators]);

  const debouncedValidate = useMemo(
    () =>
      debounce((operator: SerializedOperator, targetZoneIdentifier: string) => {
        validateInterval({
          type: 'POST',
          url: '/validations/geologic_intervals',
          data: {
            validation: {
              id: target.id,
              operatorNamingConventionsAttributes: [
                { operator, targetZoneIdentifier },
              ],
            },
          },
          onSuccess: (data) => {
            const oncError = data.errors.find((e) =>
              e.attribute.startsWith('operator_naming_conventions')
            );
            setValidationError(oncError ?? null);
            const dup = data.warnings.find(
              (w) => w.code === DUPLICATE_WARNING_CODE
            );
            setDuplicateWarning(dup ?? null);
          },
        });
      }, 400),
    [target.id, validateInterval]
  );

  useEffect(() => {
    return () => {
      debouncedValidate.cancel();
    };
  }, [debouncedValidate]);

  useEffect(() => {
    if (selectedOperator && targetZoneIdentifier.trim().length > 0) {
      debouncedValidate(
        serializeOperator(selectedOperator),
        targetZoneIdentifier.trim()
      );
    } else {
      debouncedValidate.cancel();
      setDuplicateWarning(null);
      setValidationError(null);
    }
  }, [selectedOperator, targetZoneIdentifier, debouncedValidate]);

  // Empty input → use the cached initial list (no fresh fetch); typed input
  // → the live results. "All operators" sentinel is always first.
  const operatorItems = useMemo<OperatorSearchResult[]>(() => {
    const liveResults = operatorsState?.data?.results ?? [];
    const baseResults = operatorInputEmpty
      ? (initialOperators ?? liveResults)
      : liveResults;
    return [ALL_OPERATORS_ITEM, ...baseResults];
  }, [operatorInputEmpty, initialOperators, operatorsState?.data?.results]);
  const isValidating = validateState.loading;

  const canAdd =
    !!selectedOperator &&
    targetZoneIdentifier.trim().length > 0 &&
    !validationError;

  return (
    <div className={styles.oncAddPanel}>
      <ComboBox<OperatorSearchResult>
        id="onc-operator"
        titleText="Operator"
        placeholder="Search operators…"
        items={operatorItems}
        itemToString={(item) => item?.name ?? ''}
        selectedItem={selectedOperator}
        onChange={({ selectedItem }) =>
          setSelectedOperator(selectedItem ?? null)
        }
        onInputChange={(text) => {
          const trimmed = text?.trim() ?? '';
          if (trimmed.length === 0) {
            setOperatorInputEmpty(true);
            debouncedSearchOperators.cancel();
            return;
          }
          setOperatorInputEmpty(false);
          resetOperatorsSearch(undefined);
          debouncedSearchOperators(trimmed);
        }}
        size="md"
      />
      <TextInput
        id="onc-target-zone-identifier"
        labelText="Target Zone Identifier"
        placeholder="e.g. WCA, Wolfcamp A"
        value={targetZoneIdentifier}
        onChange={(e) => setTargetZoneIdentifier(e.target.value)}
        size="md"
        warn={!!duplicateWarning}
        warnText={
          duplicateWarning
            ? buildDuplicateWarnText(
                duplicateWarning,
                targetZoneIdentifier.trim(),
                target
              )
            : undefined
        }
      />
      {validationError && (
        <InlineNotification
          kind="error"
          title="Duplicate naming convention"
          subtitle={buildValidationErrorSubtitle(
            validationError,
            selectedOperator,
            targetZoneIdentifier.trim()
          )}
          hideCloseButton
          lowContrast
        />
      )}
      {duplicateWarning && (
        <InlineNotification
          kind="warning"
          title="Duplicate landing string"
          subtitle={buildDuplicateWarningSubtitle(duplicateWarning, target)}
          hideCloseButton
          lowContrast
        />
      )}
      <div className={styles.oncAddActions}>
        <Button kind="ghost" size="sm" onClick={onCancel} type="button">
          Cancel
        </Button>
        <Button
          kind="primary"
          size="sm"
          disabled={!canAdd || isValidating}
          onClick={() => {
            if (!selectedOperator) return;
            onAdd(
              serializeOperator(selectedOperator),
              targetZoneIdentifier.trim()
            );
          }}
          type="button"
        >
          Add
        </Button>
      </div>
    </div>
  );
};
